import { Signal, WritableSignal } from '@angular/core';
import { Store, TypeOrSignalState, createStore } from '../state';

export type CommandAction = (extras?: any) => void;

export interface ICommandState {
    _id: string;
    divider: boolean;
    disabled: boolean;
    hidden: boolean;
    label: string;
    tooltip?: string;
    iconLeft?: string;
    iconLeftColor?: string;
    iconRight?: string;
    iconRightColor?: string;
    count?: number;
    data: any;
    isDefault: boolean;
}

export interface ICommand extends ICommandState {
    action: CommandAction;
    $signal(key: keyof ICommandState): Signal<any>;
    $changed: Signal<number>;
}

export class Command implements ICommand {

    private store: Store<ICommandState>;

    public get _id(): string {
        return this.store.select(s => s?._id())();
    }

    public set _id(value: string) {
        this.store.update('_id', value);
    }

    public get divider(): boolean {
        return this.store.select(s => s?.divider())();
    }

    public set divider(value: boolean) {
        this.store.update('divider', value);
    }

    public get disabled(): boolean {
        return this.store.select(s => s?.disabled())();
    }

    public set disabled(value: boolean) {
        this.store.update('disabled', value);
    }

    public get hidden(): boolean {
        return this.store.select(s => s?.hidden())();
    }

    public set hidden(value: boolean) {
        this.store.update('hidden', value);
    }

    public get isDefault(): boolean {
        return this.store.select(s => s?.isDefault())();
    }

    public set isDefault(value: boolean) {
        this.store.update('isDefault', value);
    }

    public get label(): string {
        return this.store.select(s => s?.label())();
    }

    public set label(value: string) {
        this.store.update('label', value);
    }

    public get tooltip(): string {
        return this.store.select(s => s?.tooltip())();
    }

    public set tooltip(value: string) {
        this.store.update('tooltip', value);
    }

    public get iconLeft(): string {
        return this.store.select(s => s?.iconLeft())();
    }

    public set iconLeft(value: string) {
        this.store.update('iconLeft', value);
    }

    public get iconLeftColor(): string {
        return this.store.select(s => s?.iconLeftColor())();
    }

    public set iconLeftColor(value: string) {
        this.store.update('iconLeftColor', value);
    }

    public get iconRight(): string {
        return this.store.select(s => s?.iconRight())();
    }

    public set iconRight(value: string) {
        this.store.update('iconRight', value);
    }

    public get iconRightColor(): string {
        return this.store.select(s => s?.iconRightColor())();
    }

    public set iconRightColor(value: string) {
        this.store.update('iconRightColor', value);
    }

    public get count(): number {
        return this.store.select(s => s?.count())();
    }

    public set count(value: number) {
        this.store.update('count', value);
    }

    public get data(): any {
        return this.store.select(s => s?.data())();
    }

    public set data(value: any) {
        this.store.update('data', value);
    }

    public action: CommandAction;
    public readonly $changed: Signal<number>;

    public $signal<T>(selector: keyof ICommandState): Signal<T> {
        const signal = this.store.select(x => x[selector]());
        return signal;
    }

    public clone(overrides?: Partial<ICommandState>): Command {
        const data = JSON.stringify(this.store.getValue());
        const safeOverrides = { ...overrides };
        const command = new Command({ ...JSON.parse(data), action: this.action, ...safeOverrides });
        return command;
    }

    constructor(command: Partial<TypeOrSignalState<ICommand>>) {

        const state = { ...command };
        delete state.action;

        this.action = command.action;

        this.store = createStore<ICommandState>({
            _id: '',
            divider: false,
            disabled: false,
            hidden: false,
            label: null,
            tooltip: null,
            iconLeft: null,
            iconRight: null,
            iconLeftColor: null,
            iconRightColor: null,
            count: null,
            data: null,
            isDefault: false,
            ...state
        });

        this.$changed = this.store.$changed;
    }
}